import IndicatorWrapper from './IndicatorWrapper';
import CardPerspectiveIndicator from './CardPerspectiveIndicator';
import HiddenIndicatorsCount from './HiddenIndicatorsCount';
import { type PerspectiveSummary } from '@/lib/types';
import usePerspectivesEnabled from '@/lib/utils/usePerspectivesEnabled';
import agoraWorlds, {
  type AgoraWorld,
} from '../../portfolio-item/PortfolioItemFields/FieldRenderer/TypeSpecificFieldRenderers/WorldSelect/worlds';
import WorldIndicator from './WorldIndicator';
import { type ReactNode } from 'react';

type CardPerspectiveAndWorldIndicatorsProps = {
  perspectives: PerspectiveSummary[];
  worlds: string[];
};

const CardPerspectiveAndWorldIndicators = ({
  perspectives,
  worlds,
}: CardPerspectiveAndWorldIndicatorsProps) => {
  const perspectivesEnabled = usePerspectivesEnabled();

  const validWorlds = worlds.filter(world =>
    (agoraWorlds as readonly string[]).includes(world),
  ) as AgoraWorld[];

  if (!perspectivesEnabled) {
    return (
      <IndicatorWrapper>
        {validWorlds.length > 4 ? (
          <>
            {validWorlds.slice(0, 3).map(world => (
              <WorldIndicator world={world} />
            ))}
            <HiddenIndicatorsCount count={worlds.length - 3} />
          </>
        ) : (
          validWorlds.map(world => <WorldIndicator world={world} />)
        )}
      </IndicatorWrapper>
    );
  }

  const perspectivesAndWorlds = (
    perspectives as (PerspectiveSummary | AgoraWorld)[]
  )
    .concat(validWorlds)
    .sort((a, b): number => {
      const aName = typeof a === 'string' ? a : a.name;
      const bName = typeof b === 'string' ? b : b.name;

      return aName.localeCompare(bName);
    });

  const renderPerspectiveOrWorld = (
    perspectiveOrWorld: PerspectiveSummary | AgoraWorld,
  ): ReactNode => {
    if (typeof perspectiveOrWorld === 'string') {
      return (
        <WorldIndicator world={perspectiveOrWorld} key={perspectiveOrWorld} />
      );
    }

    return (
      <CardPerspectiveIndicator
        perspective={perspectiveOrWorld}
        key={perspectiveOrWorld.id}
      />
    );
  };

  return (
    <IndicatorWrapper>
      {perspectivesAndWorlds.length > 4 ? (
        <>
          {perspectivesAndWorlds.slice(0, 3).map(renderPerspectiveOrWorld)}
          <HiddenIndicatorsCount count={perspectivesAndWorlds.length - 3} />
        </>
      ) : (
        perspectivesAndWorlds.map(renderPerspectiveOrWorld)
      )}
    </IndicatorWrapper>
  );
};

export default CardPerspectiveAndWorldIndicators;
